import { types, Instance } from 'mobx-state-tree'
import { Classes } from '@blueprintjs/core'
// Persistence disabled - theme loaded fresh from user preferences

export type Theme = 'light' | 'dark' | 'system'

/**
 * Theme store model for managing application theme state
 */
export const ThemeStore = types
  .model('ThemeStore', {
    theme: types.optional(types.enumeration(['light', 'dark', 'system']), 'light'),
  })
  .views(self => ({
    get isDark() {
      return self.theme === 'dark' || 
        (self.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    },
    get debugInfo() {
      return {
        currentTheme: self.theme,
        isDarkMode: this.isDark,
        systemPreference: typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        bodyClasses: typeof document !== 'undefined' && document.body.classList.toString()
      }
    }
  }))
  .actions(self => ({
    setTheme(newTheme: Theme) {
      self.theme = newTheme
      // Save to localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('theme', newTheme)
      }
      this.applyTheme()
    },
    
    toggleTheme() {
      // Cycle through: light -> dark -> system
      const themeOrder: Theme[] = ['light', 'dark', 'system']
      const currentIndex = themeOrder.indexOf(self.theme)
      const nextIndex = (currentIndex + 1) % themeOrder.length
      this.setTheme(themeOrder[nextIndex])
    },

    applyTheme() {
      if (typeof document === 'undefined') return
      
      const isDark = (self as any).isDark
      
      // Apply theme class to both body and root element
      document.body.classList.toggle(Classes.DARK, isDark)
      document.documentElement.classList.toggle(Classes.DARK, isDark)
      document.body.classList.toggle('bp5-dark', isDark)
      document.documentElement.classList.toggle('bp5-dark', isDark)
      
      // Set data-theme attribute
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
      
      // Set CSS variables
      document.documentElement.style.setProperty('--is-dark-theme', isDark ? '1' : '0')
    },

    afterCreate() {
      if (typeof window !== 'undefined') {
        // Restore theme from localStorage, default to 'system'
        const savedTheme = localStorage.getItem('theme') as Theme | null
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          self.theme = savedTheme
        } else {
          self.theme = 'system'
        }
        
        this.applyTheme()

        // Listen for system theme changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        mediaQuery.addEventListener('change', () => {
          if (self.theme === 'system') {
            this.applyTheme()
          }
        })
      }
    }
  }))

// Create the store instance
// REMOVED: No longer creating a singleton instance here. AppStore is responsible.
// export const themeStore = ThemeStore.create()

// DISABLED persistence to prevent MST reference conflicts
// if (typeof window !== 'undefined') {
//   const existingTheme = localStorage.getItem('theme')
//   if (existingTheme && !existingTheme.includes('"theme":')) {
//     localStorage.removeItem('theme')
//   }
//   // // persist('theme', themeStore, {
//     storage: localStorage,
//     jsonify: true,
//   }).then(() => {
//     console.log('Theme preferences restored')
//     themeStore.applyTheme()
//   }).catch(error => {
//     console.error('Error restoring theme:', error)
//     themeStore.applyTheme()
//   })
// }

// Apply default theme without persistence
// REMOVED: This logic is handled by the `afterCreate` lifecycle hook within the model itself.
// if (typeof window !== 'undefined') {
//   themeStore.applyTheme();
// }

// Export useTheme hook for functional components
// REMOVED: Components should get the theme store from the useAppStore() or useRootStore() hook.
// export const useTheme = () => themeStore

// For TypeScript
export interface IThemeStore extends Instance<typeof ThemeStore> {}

// Make the store available on window for debugging
// REMOVED: We will attach the single appStore instance to the window for debugging.
/*
declare global {
  interface Window {
    themeStore: Instance<typeof ThemeStore>;
  }
}
window.themeStore = themeStore;
*/